/**
 * Keeps a long-lived session usable across phone sleeps and backgrounded
 * tabs, so "Your session expired" stops being something people see.
 *
 * Nobody is ever signed out on a timer — Firebase Auth persistence is
 * indefinite. What goes stale is the pair of short-lived tokens every
 * request carries (the 1-hour ID token and the App Check token). Their
 * refresh is timer-driven, a sleeping phone doesn't run timers, and until
 * now nothing renewed them until a read or a save had already been
 * refused (`appCheckRecovery` is purely reactive).
 *
 * This module is the proactive half:
 *
 *   1. **Refresh on return.** When the app comes back after being away
 *      for 5+ minutes — or whenever the ID token is within 5 minutes of
 *      expiring — re-mint both tokens BEFORE the user taps anything.
 *   2. **Re-check on return if the bar is already up.** A session that
 *      was refused before the app went away is verified again, so the
 *      bar can come down by itself.
 *   3. **Reload on return if App Check locked itself out.** The SDK's 24h
 *      throttle lives in memory and only a reload clears it. If it
 *      tripped while nobody was looking, reload for them instead of
 *      showing a bar that asks them to — unless a form is in progress,
 *      in which case the bar stays and the choice is theirs.
 *
 * "Away" is measured two ways because iOS does not reliably deliver
 * `visibilitychange → hidden` before suspending a PWA: the time since the
 * tab was hidden, and the gap in a 30-second heartbeat (a suspended page
 * runs no timers, so a long gap means the page was frozen).
 *
 * Like `appCheckRecovery`, firebase is only pulled in with a dynamic
 * import at the moment it's needed.
 */
import {
  getSessionDegraded,
  recheckDegradedSession,
  refreshSessionQuietly,
  subscribeSessionDegraded,
} from './appCheckRecovery';
import { logger } from './logger';

/** Away this long → refresh on return, whatever the token's age. */
export const AWAY_REFRESH_MS = 5 * 60 * 1000;
/** ID token this close to expiry → refresh even after a short absence. */
export const TOKEN_EXPIRY_MARGIN_MS = 5 * 60 * 1000;
/** Floor between token-expiry checks (focus events arrive in bursts). */
export const MIN_CHECK_INTERVAL_MS = 60 * 1000;
/** Heartbeat period; a gap far beyond this means the page was frozen. */
export const HEARTBEAT_MS = 30 * 1000;
/** A throttle that trips this soon after a long absence counts as idle-time. */
export const THROTTLE_RELOAD_WINDOW_MS = 15 * 1000;
/** At most one automatic reload per this window (loop guard). */
export const AUTO_RELOAD_GUARD_MS = 10 * 60 * 1000;

export const AUTO_RELOAD_GUARD_KEY = 'twb:sessionAutoReloadAt';

const TEXT_ENTRY_SELECTOR = [
  'textarea',
  'input:not([type])',
  'input[type="text"]',
  'input[type="number"]',
  'input[type="search"]',
  'input[type="email"]',
  'input[type="tel"]',
  'input[type="url"]',
  'input[type="date"]',
  'input[type="datetime-local"]',
  'input[type="password"]',
].join(',');

/**
 * True when reloading could throw away something the user typed: an open
 * dialog, any text-like field holding a value, or a form control that
 * can't be read back cheaply (sliders, file pickers). Deliberately
 * conservative — a false "yes" only means the Reload bar is shown
 * instead of an automatic reload.
 */
export const hasFormInProgress = (): boolean => {
  if (typeof document === 'undefined') return false;
  if (document.querySelector('[role="dialog"], [aria-modal="true"]')) return true;
  if (document.querySelector('input[type="range"], input[type="file"]')) return true;
  const fields = document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
    TEXT_ENTRY_SELECTOR,
  );
  for (const field of fields) {
    if (field.value.trim() !== '') return true;
  }
  return false;
};

const reloadGuardAllows = (now: number): boolean => {
  try {
    const last = Number(sessionStorage.getItem(AUTO_RELOAD_GUARD_KEY) ?? 0);
    return !Number.isFinite(last) || now - last >= AUTO_RELOAD_GUARD_MS;
  } catch {
    // No sessionStorage (private mode quirks) → no way to guard a loop,
    // so don't reload automatically at all.
    return false;
  }
};

const markReload = (now: number): void => {
  try {
    sessionStorage.setItem(AUTO_RELOAD_GUARD_KEY, String(now));
  } catch {
    /* unreachable in practice: reloadGuardAllows already refused */
  }
};

export interface SessionKeepAliveOptions {
  /** Injected for tests; defaults to a real page reload. */
  reload?: () => void;
}

/**
 * Start watching for the app coming back to the foreground. Returns the
 * teardown fn (tests; production installs once for the page lifetime).
 */
export const installSessionKeepAlive = (
  options: SessionKeepAliveOptions = {},
): (() => void) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return () => {};
  }
  const reload = options.reload ?? (() => window.location.reload());

  let hiddenAt: number | null = null;
  let lastBeatAt = Date.now();
  // The most recent return from a long absence, for the throttle window.
  let longReturnAt: number | null = null;
  // A long absence that hasn't been answered with a refresh yet.
  let refreshOwed = false;
  // A laptop wakes before its Wi-Fi does: the first refresh after a long
  // absence often fails for want of a network. Allow ONE re-arm per
  // absence so the `online` event gets to finish the job.
  let owedRetryUsed = false;
  let lastCheckAt = 0;
  let inFlight: Promise<void> | null = null;
  let throttledWhileHidden = false;

  const isVisible = (): boolean => document.visibilityState === 'visible';

  /** Reload if App Check locked itself out while nobody was using the app. */
  const maybeReloadForThrottle = (): boolean => {
    const state = getSessionDegraded();
    if (!state.degraded || state.reason !== 'throttle') return false;
    if (!isVisible()) return false;
    const now = Date.now();
    const justBack =
      longReturnAt !== null && now - longReturnAt <= THROTTLE_RELOAD_WINDOW_MS;
    if (!throttledWhileHidden && !justBack) return false;
    if (hasFormInProgress()) return false;
    if (!reloadGuardAllows(now)) return false;
    markReload(now);
    logger.warn('[sessionKeepAlive] App Check throttled while idle — reloading');
    reload();
    return true;
  };

  const maybeRefresh = (): void => {
    if (inFlight) return;
    const now = Date.now();
    const owed = refreshOwed;
    if (!owed && now - lastCheckAt < MIN_CHECK_INTERVAL_MS) return;
    refreshOwed = false;
    lastCheckAt = now;
    inFlight = (async () => {
      try {
        const { auth } = await import('../config/firebase');
        const user = auth.currentUser;
        if (!user) return;
        let needed = owed;
        if (!needed) {
          // Reads the cached token; no network unless it already expired
          // (in which case the SDK renews it here, which is the point).
          const result = await user.getIdTokenResult();
          const expiresAt = Date.parse(result.expirationTime);
          needed =
            Number.isFinite(expiresAt) &&
            expiresAt - Date.now() < TOKEN_EXPIRY_MARGIN_MS;
        }
        if (!needed) return;
        const refreshed = await refreshSessionQuietly();
        logger.debug(
          `[sessionKeepAlive] refresh on return ${refreshed ? 'succeeded' : 'did not complete'}`,
        );
        if (!refreshed && owed && !owedRetryUsed) {
          owedRetryUsed = true;
          refreshOwed = true;
        }
      } catch (err) {
        logger.warn('[sessionKeepAlive] refresh on return failed', err);
      } finally {
        inFlight = null;
      }
    })();
  };

  /** Any signal that the user is (back) in front of the app. */
  const onPresent = (): void => {
    if (!isVisible()) return;
    const now = Date.now();
    const hiddenFor = hiddenAt === null ? 0 : now - hiddenAt;
    const frozenFor = now - lastBeatAt;
    hiddenAt = null;
    lastBeatAt = now;
    if (Math.max(hiddenFor, frozenFor) >= AWAY_REFRESH_MS) {
      longReturnAt = now;
      refreshOwed = true;
      owedRetryUsed = false;
    }
    if (maybeReloadForThrottle()) return;
    const degraded = getSessionDegraded();
    if (degraded.degraded && degraded.reason === 'permission') {
      // The bar is up from before the app went away. Check again with
      // fresh tokens (the re-check mints them) — if the session is fine
      // now, the bar comes down without a reload.
      refreshOwed = false;
      void recheckDegradedSession();
      return;
    }
    maybeRefresh();
  };

  const onHidden = (): void => {
    if (hiddenAt === null) hiddenAt = Date.now();
  };

  const onVisibilityChange = (): void => {
    if (isVisible()) onPresent();
    else onHidden();
  };

  const beat = (): void => {
    // A beat that arrives long after the previous one means the page was
    // frozen in between — handle it as a return even if no event fired.
    if (isVisible() && Date.now() - lastBeatAt >= AWAY_REFRESH_MS) {
      onPresent();
      return;
    }
    if (isVisible()) lastBeatAt = Date.now();
  };

  const unsubscribeDegraded = subscribeSessionDegraded((state) => {
    if (state.degraded && state.reason === 'throttle') {
      throttledWhileHidden = !isVisible();
      // Tripped in the first moments after coming back (the overdue
      // refresh timer fires right as the page wakes) → same as idle-time.
      maybeReloadForThrottle();
    } else if (!state.degraded) {
      throttledWhileHidden = false;
    }
  });

  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', onHidden);
  window.addEventListener('pageshow', onPresent);
  window.addEventListener('online', onPresent);
  window.addEventListener('focus', onPresent);
  const heartbeat = setInterval(beat, HEARTBEAT_MS);

  return () => {
    unsubscribeDegraded();
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('pagehide', onHidden);
    window.removeEventListener('pageshow', onPresent);
    window.removeEventListener('online', onPresent);
    window.removeEventListener('focus', onPresent);
    clearInterval(heartbeat);
  };
};
